<template>
  <div class="column content-box">
    <woot-button
      color-scheme="success"
      class-names="button--fixed-top"
      icon="add-circle"
      @click="openAddPopup()"
    >
      {{ $t('AGENT_BOT_MANAGEMENT.HEADER_BTN_TXT') }}
    </woot-button>

    <div class="row full-width">
      <div class="columns list-column">
        <p
          v-if="!uiFlags.fetchingList && !records.length"
          class="no-items-error-message"
        >
          {{ $t('AGENT_BOT_MANAGEMENT.LIST.404') }}
        </p>

        <woot-loading-state
          v-if="uiFlags.fetchingList"
          :message="$t('AGENT_BOT_MANAGEMENT.LOADING')"
        />

        <table
          v-if="!uiFlags.fetchingList && records.length"
          class="woot-table"
        >
          <thead>
            <tr>
              <th>{{ $t('AGENT_BOT_MANAGEMENT.TABLE.NAME') }}</th>
              <th>{{ $t('AGENT_BOT_MANAGEMENT.TABLE.DESCRIPTION') }}</th>
              <th>{{ $t('AGENT_BOT_MANAGEMENT.TABLE.ACTIONS') }}</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="bot in records" :key="bot.id">
              <td class="name-cell">
                <div class="bot-name-wrapper">
                  <div class="bot-avatar">
                    <img
                      v-if="bot.avatar_url"
                      :src="bot.avatar_url"
                      alt="avatar"
                    />
                  </div>
                  <span class="bot-name">{{ bot.name }}</span>
                </div>
              </td>

              <td class="wrap-break-words">
                {{ bot.description }}
              </td>

              <td class="button-wrapper">
                <woot-button
                  v-tooltip.top="$t('AGENT_BOT_MANAGEMENT.ACTIONS.EDIT')"
                  variant="smooth"
                  size="small"
                  color-scheme="secondary"
                  icon="edit"
                  class-names="action-btn"
                  @click="openEditPopup(bot)"
                />

                <woot-button
                  v-tooltip.top="$t('AGENT_BOT_MANAGEMENT.ACTIONS.DELETE')"
                  variant="smooth"
                  color-scheme="alert"
                  size="small"
                  icon="dismiss-circle"
                  class-names="action-btn"
                  :is-loading="loading[bot.id]"
                  @click="openDeletePopup(bot)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="columns sidebar">
        <p>
          {{ $t('AGENT_BOT_MANAGEMENT.SIDEBAR.DESCRIPTION_1') }}
        </p>

        <p>
          {{ $t('AGENT_BOT_MANAGEMENT.SIDEBAR.DESCRIPTION_2') }}
        </p>

        <p>
          {{ $t('AGENT_BOT_MANAGEMENT.SIDEBAR.DESCRIPTION_3') }}
        </p>

        <p class="highlight">
          {{ $t('AGENT_BOT_MANAGEMENT.SIDEBAR.HIGHLIGHT') }}
        </p>

        <p>
          {{ $t('AGENT_BOT_MANAGEMENT.SIDEBAR.SUPPORT') }}
        </p>
      </div>
    </div>

    <woot-modal :show.sync="showAddPopup" :on-close="hideAddPopup">
      <add-agent-bots :on-close="hideAddPopup" />
    </woot-modal>

    <woot-modal :show.sync="showEditPopup" :on-close="hideEditPopup">
      <edit-agent-bots
        v-if="showEditPopup"
        :bot="selectedBot"
        :on-close="hideEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      :show.sync="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('AGENT_BOT_MANAGEMENT.DELETE.TITLE')"
      :message="$t('AGENT_BOT_MANAGEMENT.DELETE.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="$t('AGENT_BOT_MANAGEMENT.DELETE.CONFIRM')"
      :reject-text="$t('AGENT_BOT_MANAGEMENT.DELETE.REJECT')"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import AddAgentBots from './AddAgentBots';
import EditAgentBots from './EditAgentBots';

export default {
  components: {
    AddAgentBots,
    EditAgentBots,
  },

  data() {
    return {
      loading: {},
      showAddPopup: false,
      showEditPopup: false,
      showDeleteConfirmationPopup: false,
      selectedBot: {},
    };
  },

  computed: {
    ...mapGetters({
      records: 'getAgentBotManagement',
      uiFlags: 'agentBots/getUIFlags',
    }),

    deleteMessage() {
      return ` ${this.selectedBot.name || ''}?`;
    },
  },

  mounted() {
    this.$store.dispatch('getAgentBotManagement');
  },

  methods: {
    openAddPopup() {
      this.showAddPopup = true;
    },

    hideAddPopup() {
      this.showAddPopup = false;
    },

    openEditPopup(bot) {
      this.showEditPopup = true;
      this.selectedBot = bot;
    },

    hideEditPopup() {
      this.showEditPopup = false;
    },

    openDeletePopup(bot) {
      this.showDeleteConfirmationPopup = true;
      this.selectedBot = bot;
    },

    closeDeletePopup() {
      this.showDeleteConfirmationPopup = false;
    },

    confirmDeletion() {
      this.loading[this.selectedBot.id] = true;
      this.closeDeletePopup();
      this.deleteAgentBotManagement(this.selectedBot.id);
    },

    deleteAgentBotManagement(id) {
      this.$store.dispatch('deleteAgentBotManagement', id);
    },
  },
};
</script>

<style scoped>
.content-box {
  position: static;
  z-index: auto;
  overflow: visible;
}

.full-width {
  display: flex;
  gap: 24px;
  position: static;
  z-index: auto;
  overflow: visible;
}

.list-column {
  flex: 2;
  position: static;
  z-index: auto;
  overflow: visible;
}

.sidebar {
  flex: 1;
  font-size: 14px;
  line-height: 1.6;
  color: #000;
  position: static;
  z-index: auto;
  overflow: visible;
}

.name-cell {
  width: 24rem;
}

.bot-name-wrapper {
  display: flex;
  align-items: center;
  gap: 14px;
}

.bot-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #f2f2f2;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.bot-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.bot-name {
  font-weight: 600;
  font-size: 15px;
}

.wrap-break-words {
  word-break: break-word;
  white-space: normal;
}

.button-wrapper {
  display: flex;
  align-items: center;
  gap: 5px;
}

.action-btn {
  padding: 4px;
  min-width: 32px;
  width: 32px;
  height: 32px;
}

.highlight {
  font-weight: 600;
}

:deep(.woot-modal),
:deep(.modal-container),
:deep(.modal-mask) {
  z-index: 999999;
}
</style>
